"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import SearchModalTrigger from "@/components/ui/SearchModalTrigger";
import { useAuthSession } from "@/components/providers/AuthSessionProvider";
import { useProfile } from "@/lib/hooks/useProfile";
import { getLocaleFromPathname } from "@/i18n/routing";
import { localePath } from "@/lib/locale-path";
import { NotificationBell } from "@/components/dashboard/notifications/NotificationBell";
import { HeaderNavDropdown } from "./HeaderNavDropdown";
import { Search } from "lucide-react";

export interface NavLink {
  key: string;
  label: string;
  href: string;
  description?: string;
  section?: "discover" | "compare";
}

export interface NavGroup {
  key: string;
  label: string;
  items: ReadonlyArray<NavLink>;
}

export type NavEntry = NavLink | NavGroup;

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return "items" in entry;
}

interface HeaderNavClientProps {
  entries: ReadonlyArray<NavEntry>;
}

/**
 * Active state ve mobile drawer state icin client island.
 * Search input + buttons da burada tutulur (kucuk JS yuku).
 */
export default function HeaderNavClient({ entries }: HeaderNavClientProps) {
  const pathname = usePathname() ?? "/";
  const locale = getLocaleFromPathname(pathname);
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const { user, loading: authLoading } = useAuthSession();
  const authenticated = !authLoading && Boolean(user);
  const { data: profile } = useProfile({ enabled: authenticated });

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    drawerRef.current?.querySelector<HTMLElement>("a,summary,button")?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  const isActive = (href: string): boolean => {
    const localPath = pathname.replace(/^\/(?:tr|en)(?=\/|$)/, "") || "/";
    if (href === "/") return localPath === "/";
    return localPath === href || localPath.startsWith(`${href}/`);
  };

  return (
    <>
      {/* Desktop nav */}
      <nav className="hidden lg:flex items-center gap-1">
        {entries.map((entry) =>
          isNavGroup(entry) ? (
            <HeaderNavDropdown key={entry.key} group={entry} isActive={isActive} />
          ) : (
            <Link
              key={entry.key}
              href={entry.href}
              prefetch={false}
              className={clsx(
                "relative px-3 py-5 text-[14px] font-medium transition-colors after:absolute after:inset-x-3 after:bottom-3 after:h-0.5 after:origin-center after:scale-x-0 after:bg-(--color-accent) after:transition-transform",
                isActive(entry.href)
                  ? "text-(--color-foreground) after:scale-x-100"
                  : "text-(--color-foreground) hover:text-(--color-brand)",
              )}
            >
              {entry.label}
            </Link>
          ),
        )}
      </nav>

      {/* Search bar */}
      <button type="button" onClick={() => document.dispatchEvent(new Event("open-search"))} aria-label="Ürün, şehir veya analiz ara" className="hidden xl:inline-flex h-11 w-11 shrink-0 items-center justify-center text-(--color-foreground) transition-colors hover:text-(--color-brand)">
        <svg
          width="14"
          height="14"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-(--color-muted) group-hover:text-(--color-brand) transition-colors shrink-0"
          aria-hidden
        >
          <circle cx="9" cy="9" r="6" />
          <path d="m17 17-3.5-3.5" strokeLinecap="round" />
        </svg>
      </button>
      {/* CTA buttons */}
      <div className="hidden lg:flex items-center gap-3 shrink-0">
        {authenticated && user ? (
          <>
            <NotificationBell locale={locale} />
            <Link
              href={localePath(locale, "/hesabim")} prefetch={false}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-border px-3 text-[13px] font-medium text-foreground bg-bg-alt/50 hover:border-brand/40 transition-colors"
            >
              {profile?.avatar_url ? (
                <Image
                  src={profile.avatar_url}
                  alt=""
                  width={28}
                  height={28}
                  unoptimized
                  className="h-7 w-7 rounded-full object-cover shrink-0"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/10 text-[11px] font-bold text-brand shrink-0">
                  {(user.full_name ?? user.email ?? "?").charAt(0).toUpperCase()}
                </span>
              )}
              {user.full_name?.split(" ")[0] ?? user.email ?? "Hesabım"}
            </Link>
            <Link href={localePath(locale, "/ilan-ver")} prefetch={false} className="h-10 px-5 inline-flex items-center justify-center rounded-xl bg-(--color-brand) text-(--color-brand-fg) text-[13px] font-bold hover:bg-(--color-brand-dark) transition-all active:scale-95 whitespace-nowrap">
              İlan Ver
            </Link>
          </>
        ) : (
          <>
            <Link
              href={localePath(locale, "/giris")} prefetch={false}
              className="h-10 px-5 inline-flex items-center justify-center rounded-xl border border-(--color-border) text-[13px] font-semibold text-(--color-foreground) hover:bg-(--color-bg-alt) hover:text-(--color-brand) transition-all active:scale-95 whitespace-nowrap"
            >
              Giriş Yap
            </Link>
            <Link
              href={localePath(locale, "/ilan-ver")} prefetch={false}
              className="h-10 px-5 inline-flex items-center justify-center rounded-xl bg-(--color-brand) text-(--color-brand-fg) text-[13px] font-bold hover:bg-(--color-brand-dark) hover:scale-[1.02] transition-all active:scale-95 shadow-lg shadow-(--color-brand)/20 whitespace-nowrap"
            >
              İlan Ver
            </Link>
          </>
        )}
      </div>

      {/* Mobil arama — ana görev drawer içine saklanmaz. */}
      <button
        type="button"
        onClick={() => document.dispatchEvent(new Event("open-search"))}
        aria-label="Ürün veya hal ara"
        className="ml-auto inline-flex h-11 w-11 items-center justify-center rounded-[8px] border border-(--color-border) bg-(--color-surface) text-(--color-foreground) lg:hidden"
      >
        <Search className="h-5 w-5" aria-hidden="true" />
      </button>

      <Link href={localePath(locale, authenticated ? "/hesabim" : "/giris")} prefetch={false}
        className="hidden h-11 shrink-0 items-center justify-center rounded-lg border border-(--color-border) px-2 text-xs font-bold text-(--color-brand) min-[430px]:inline-flex lg:hidden">
        {authenticated ? "Hesabım" : "Giriş"}
      </Link>

      {/* Mobile toggle */}
      <button
        ref={toggleRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Menüyü aç/kapat"
        aria-expanded={open}
        className="inline-flex h-11 w-11 items-center justify-center rounded-[8px] bg-(--color-brand) text-(--color-brand-fg) shadow-sm transition-transform active:scale-95 lg:hidden"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5">
          {open ? (
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          ) : (
            <path d="M4 6h12M4 10h12M4 14h12" strokeLinecap="round" />
          )}
        </svg>
      </button>

      {/* Mobile drawer */}
      {open && (
        <div ref={drawerRef} className="lg:hidden absolute left-0 right-0 top-full max-h-[calc(100dvh_-_5rem_-_env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain border-t border-(--color-border) bg-(--color-surface) pb-[env(safe-area-inset-bottom)] shadow-lg">
          <nav aria-label="Mobil menü" className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            <div className="grid grid-cols-2 gap-2 border-b border-(--color-border) pb-3 mb-2">
              <Link onClick={() => setOpen(false)} href={localePath(locale, authenticated ? "/hesabim" : "/giris")} className="flex min-h-11 items-center justify-center rounded-lg border border-(--color-brand) text-sm font-bold text-(--color-brand)">{authenticated ? "Hesabım" : "Giriş yap"}</Link>
              <Link onClick={() => setOpen(false)} href={localePath(locale, authenticated ? "/hesabim/ilanlarim" : "/kayit")} className="flex min-h-11 items-center justify-center rounded-lg border border-(--color-border) text-sm font-bold">{authenticated ? "İlanlarım" : "Üye ol"}</Link>
              <Link onClick={() => setOpen(false)} href={localePath(locale, "/ilan-ver")} className="col-span-2 flex min-h-11 items-center justify-center rounded-lg bg-(--color-brand) text-sm font-bold text-(--color-brand-fg)">+ İlan ver</Link>
            </div>
            {entries.map((entry) =>
              isNavGroup(entry) ? (
                <details key={entry.key} className="group border-b border-(--color-border-soft) py-1">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 text-sm font-semibold"><span>{entry.label}</span><span className="transition-transform group-open:rotate-45" aria-hidden>+</span></summary>
                  <div className="pb-2 pl-3">
                    {entry.items.map((item) => (
                      <Link
                        key={item.key}
                        href={item.href}
                        prefetch={false}
                        className={clsx(
                          "block min-h-11 border-l-2 px-3 py-3 text-sm font-medium",
                          isActive(item.href)
                            ? "border-(--color-accent) bg-(--color-brand-light) text-(--color-foreground)"
                            : "border-transparent text-(--color-foreground) hover:bg-(--color-bg-alt)",
                        )}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </details>
              ) : (
                <Link
                  key={entry.key}
                  href={entry.href}
                  prefetch={false}
                  className={clsx(
                    "px-3 py-3 rounded-md text-sm font-medium",
                    isActive(entry.href)
                      ? "text-(--color-brand) bg-(--color-brand-light)"
                      : "text-(--color-foreground) hover:bg-(--color-bg-alt)",
                  )}
                >
                  {entry.label}
                </Link>
              ),
            )}
            {authenticated && user && (
              <Link
                href={localePath(locale, "/hesabim")} prefetch={false}
                className={clsx(
                  "px-3 py-3 rounded-md text-sm font-medium",
                  isActive("/hesabim")
                    ? "text-(--color-brand) bg-(--color-brand-light)"
                    : "text-(--color-foreground) hover:bg-(--color-bg-alt)",
                )}
              >
                Hesabım
              </Link>
            )}
            <div className="mt-2 flex items-center gap-2 border-t border-(--color-border) pt-3">
              {authenticated ? (
                <Link href={localePath(locale, "/ilan-ver")} prefetch={false} className="flex-1 h-11 inline-flex items-center justify-center rounded-xl bg-(--color-brand) text-(--color-brand-fg) text-sm font-bold transition-all active:scale-95">
                  İlan Ver
                </Link>
              ) : (
                <>
                  <Link
                    href={localePath(locale, "/giris")} prefetch={false}
                    className="flex-1 h-11 inline-flex items-center justify-center rounded-xl border border-(--color-border) text-sm font-semibold hover:bg-(--color-bg-alt) transition-all active:scale-95"
                  >
                    Giriş Yap
                  </Link>
                  <Link
                    href={localePath(locale, "/ilan-ver")} prefetch={false}
                    className="flex-1 h-11 inline-flex items-center justify-center rounded-xl bg-(--color-brand) text-(--color-brand-fg) text-sm font-bold shadow-md shadow-(--color-brand)/10 active:scale-95 transition-all"
                  >
                    İlan Ver
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}

      <SearchModalTrigger />
    </>
  );
}
